/**
 * @fileoverview Cell/header renderers for the Day-One columns (MOR-971).
 *
 * Split out of `columns.tsx` (react-refresh/only-export-components) — these
 * are the small components the column defs reference for header/cell
 * rendering, kept separate from the pure column-def data + helpers.
 */

import {
  type Column,
  type Table as TanStackTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Badge } from "@/ui/primitives";
import { cn } from "@/utils/misc";
import {
  ROLE_META,
  FIRM_CATEGORY_META,
  type ContactRole,
} from "@/lib/contact-roles";
import type { DayOnePrecedentRow } from "./types";
import { getMeta } from "./columns.meta";

/**
 * Sortable column header — renders the label with a tri-state arrow
 * indicator (none / asc / desc) and toggles sort on click. Mirrors the
 * pattern used by the Job Contacts table for visual consistency.
 */
export function SortableHeader({
  column,
  label,
}: {
  column: Column<DayOnePrecedentRow, unknown>;
  label: string;
}) {
  const sort = column.getIsSorted();
  const Icon =
    sort === "asc" ? ArrowUp : sort === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => column.toggleSorting(sort === "asc")}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-1 text-xs font-semibold uppercase tracking-wider transition-colors",
        "hover:bg-muted/60 focus:outline-none focus:ring-1 focus:ring-ring",
        sort ? "text-foreground" : "text-muted-foreground",
      )}
    >
      <span>{label}</span>
      <Icon
        className={cn(
          "h-3 w-3 transition-opacity",
          sort ? "opacity-100" : "opacity-40",
        )}
        aria-hidden
      />
    </button>
  );
}

export function ContactCell({
  row,
  table,
}: {
  row: { original: DayOnePrecedentRow };
  table: TanStackTable<DayOnePrecedentRow>;
}) {
  const {
    contactDisplayName,
    contactRoleKind,
    firmContactCategory,
    contactExists,
    jobContactId,
    firmContactId,
  } = row.original;
  if (!jobContactId && !firmContactId) {
    // Non-fanned-out documents (no `cr:*` / `fc:*` tag on the source
    // template) get an em-dash placeholder so the column reads cleanly.
    return <span className="text-xs text-muted-foreground/40">—</span>;
  }
  // Badge meta: job-contact roles + firm categories use the same
  // `badgeColor` palette as the Contacts / Firm Contacts tables.
  const meta = contactRoleKind
    ? ROLE_META[contactRoleKind as ContactRole]
    : firmContactCategory
      ? FIRM_CATEGORY_META[firmContactCategory]
      : null;
  const Icon = meta?.icon;
  // Deleted contacts can't open a detail panel — render them inert.
  const clickable = contactExists;
  const nameRow = (
    <>
      {Icon && <Icon className="size-3 shrink-0 text-muted-foreground" />}
      <span
        className={cn(
          "truncate text-sm font-medium",
          contactExists ? "text-foreground" : "italic text-muted-foreground",
        )}
        title={
          contactExists
            ? (contactDisplayName ?? undefined)
            : `${contactDisplayName ?? "Contact"} (deleted)`
        }
      >
        {contactDisplayName || "Unnamed contact"}
      </span>
    </>
  );
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {clickable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            getMeta(table).onOpenContact(row.original);
          }}
          className="flex min-w-0 items-center gap-1.5 rounded-sm text-left underline-offset-2 hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {nameRow}
        </button>
      ) : (
        <div className="flex min-w-0 items-center gap-1.5">{nameRow}</div>
      )}
      {meta && (
        <Badge
          color={meta.badgeColor}
          variant="soft"
          size="sm"
          className="self-start whitespace-nowrap"
        >
          {meta.label}
        </Badge>
      )}
    </div>
  );
}
